import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Paper,
  Stack,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DescriptionIcon from '@mui/icons-material/Description';
import FilterListIcon from '@mui/icons-material/FilterList';
import { saveAs } from 'file-saver';
import { generateReceiptGroupPdf, ReceiptGroupPrintData } from 'src/helpers/receiptGroup-helper';
import { DataService } from 'src/services/data-service';

interface PrintOptionsDialogProps {
  open: boolean;
  onClose: () => void;
  receiptGroupRefNumber: string | null;
}

const PrintOptionsDialog: React.FC<PrintOptionsDialogProps> = ({
  open,
  onClose,
  receiptGroupRefNumber
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Fee type selection state
  const [feeTypeSelectorOpen, setFeeTypeSelectorOpen] = useState(false);
  const [availableFeeTypes, setAvailableFeeTypes] = useState<string[]>([]);
  const [selectedFeeTypes, setSelectedFeeTypes] = useState<string[]>([]);
  const [feeTypeAction, setFeeTypeAction] = useState<'print' | 'preview' | 'download' | null>(null);

  useEffect(() => {
    if (open && receiptGroupRefNumber) {
      fetchAvailableFeeTypes();
    }
  }, [open, receiptGroupRefNumber]);

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const fetchAvailableFeeTypes = async () => {
    try {
      const response = await DataService.get(
        `api/ReceiptGroup/print/${receiptGroupRefNumber}?installments=none`
      );

      if (response.ok) {
        const responseData = await response.json();
        let groupData: any;

        if (responseData.success && responseData.data) {
          groupData = responseData.data;
        } else if (responseData.id) {
          groupData = responseData;
        } else {
          return;
        }

        // Extract unique fee types from receipts
        const feeTypesSet = new Set<string>();
        groupData.receipts?.forEach((receipt: any) => {
          const feeType = receipt.installment?.fee?.type;
          if (feeType) {
            feeTypesSet.add(feeType);
          }
        });

        const feeTypes = Array.from(feeTypesSet);
        setAvailableFeeTypes(feeTypes);
        setSelectedFeeTypes(feeTypes); // Select all by default
      }
    } catch (error) {
      console.error('Error fetching fee types:', error);
    }
  };

  const fetchPdfData = async (installmentsParam: string, feeTypes?: string[]): Promise<ReceiptGroupPrintData> => {
    try {
      const response = await DataService.get(
        `api/ReceiptGroup/print/${receiptGroupRefNumber}?installments=${installmentsParam}`
      );

      if (response.ok) {
        const responseData = await response.json();
        let groupData: any;

        if (responseData.success && responseData.data) {
          groupData = responseData.data;
        } else if (responseData.id) {
          groupData = responseData;
        } else {
          throw new Error('Invalid response structure');
        }

        // Filter receipts by fee types if provided
        let filteredReceipts = groupData.receipts;
        let filteredTotalAmount = groupData.totalAmount;

        if (feeTypes && feeTypes.length > 0 && installmentsParam === 'none') {
          filteredReceipts = groupData.receipts.filter((receipt: any) => {
            const receiptFeeType = receipt.installment?.fee?.type;
            return receiptFeeType && feeTypes.includes(receiptFeeType);
          });

          // Recalculate total amount for filtered receipts
          filteredTotalAmount = filteredReceipts.reduce((sum: number, receipt: any) => sum + receipt.amount, 0);
        }

        return {
          id: groupData.id,
          refNumber: groupData.refNumber,
          description: groupData.description,
          groupDate: groupData.groupDate,
          receiptCount: filteredReceipts.length,
          totalAmount: filteredTotalAmount,
          receipts: filteredReceipts,
          tenant: groupData.tenant,
          lease: groupData.lease,
          installments: groupData.installments,
          includeInstallments: installmentsParam !== 'none'
        };
      } else {
        throw new Error(`Failed to fetch receipt group: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error fetching receipt group for PDF:', error);
      throw error;
    }
  };

  const handlePrintWithInstallments = async () => {
    setLoading(true);
    setError(null);

    try {
      const pdfData = await fetchPdfData('all');
      const { blobUrl } = await generateReceiptGroupPdf(pdfData);
      const win = window.open(blobUrl);
      if (!win) {
        throw new Error('Failed to open print window. Please check your popup blocker settings.');
      }
    } catch (error) {
      console.error('Error printing receipt group:', error);
      setError(error instanceof Error ? error.message : 'Failed to print receipt group. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintWithoutInstallments = async () => {
    setLoading(true);
    setError(null);

    try {
      const pdfData = await fetchPdfData('none');
      const { blobUrl } = await generateReceiptGroupPdf(pdfData);
      const win = window.open(blobUrl);
      if (!win) {
        throw new Error('Failed to open print window. Please check your popup blocker settings.');
      }
    } catch (error) {
      console.error('Error printing receipt:', error);
      setError(error instanceof Error ? error.message : 'Failed to print receipt. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewWithInstallments = async () => {
    setLoading(true);
    setError(null);

    try {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }

      const pdfData = await fetchPdfData('all');
      const { blobUrl } = await generateReceiptGroupPdf(pdfData);
      setPdfUrl(blobUrl);
      setPreviewOpen(true);
    } catch (error) {
      console.error('Error generating preview:', error);
      setError(error instanceof Error ? error.message : 'Failed to generate preview. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewWithoutInstallments = async () => {
    setLoading(true);
    setError(null);

    try {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }

      const pdfData = await fetchPdfData('none');
      const { blobUrl } = await generateReceiptGroupPdf(pdfData);
      setPdfUrl(blobUrl);
      setPreviewOpen(true);
    } catch (error) {
      console.error('Error generating preview:', error);
      setError(error instanceof Error ? error.message : 'Failed to generate preview. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadWithInstallments = async () => {
    setLoading(true);
    setError(null);

    try {
      const pdfData = await fetchPdfData('all');
      const { blob } = await generateReceiptGroupPdf(pdfData);
      const filename = `Receipt-${receiptGroupRefNumber}-with-installments.pdf`;
      saveAs(blob, filename);
    } catch (error) {
      console.error('Error downloading receipt group:', error);
      setError(error instanceof Error ? error.message : 'Failed to download receipt. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadWithoutInstallments = async () => {
    setLoading(true);
    setError(null);

    try {
      const pdfData = await fetchPdfData('none');
      const { blob } = await generateReceiptGroupPdf(pdfData);
      const filename = `ReceiptGroup-${receiptGroupRefNumber}.pdf`;
      saveAs(blob, filename);
    } catch (error) {
      console.error('Error downloading receipt:', error);
      setError(error instanceof Error ? error.message : 'Failed to download receipt. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenInNewTab = async () => {
    if (!pdfUrl) return;
    window.open(pdfUrl, '_blank');
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  const handleClose = () => {
    handleClosePreview();
    setError(null);
    setFeeTypeSelectorOpen(false);
    setFeeTypeAction(null);
    onClose();
  };

  // Fee type selection handlers
  const handleOpenFeeTypeSelector = (action: 'print' | 'preview' | 'download') => {
    setFeeTypeAction(action);
    setFeeTypeSelectorOpen(true);
  };

  const handleCloseFeeTypeSelector = () => {
    setFeeTypeSelectorOpen(false);
    setFeeTypeAction(null);
  };

  const handleToggleFeeType = (feeType: string) => {
    setSelectedFeeTypes((prev) =>
      prev.includes(feeType)
        ? prev.filter((ft) => ft !== feeType)
        : [...prev, feeType]
    );
  };

  const handleSelectAllFeeTypes = () => {
    setSelectedFeeTypes(availableFeeTypes);
  };

  const handleDeselectAllFeeTypes = () => {
    setSelectedFeeTypes([]);
  };

  const handleConfirmFeeTypeSelection = async () => {
    if (selectedFeeTypes.length === 0) {
      setError('Please select at least one fee type.');
      return;
    }

    setFeeTypeSelectorOpen(false);

    // Execute the action with selected fee types
    if (feeTypeAction === 'print') {
      await handlePrintWithSelectedFeeTypes();
    } else if (feeTypeAction === 'preview') {
      await handlePreviewWithSelectedFeeTypes();
    } else if (feeTypeAction === 'download') {
      await handleDownloadWithSelectedFeeTypes();
    }
  };

  const handlePrintWithSelectedFeeTypes = async () => {
    setLoading(true);
    setError(null);

    try {
      const pdfData = await fetchPdfData('none', selectedFeeTypes);
      const { blobUrl } = await generateReceiptGroupPdf(pdfData);
      const win = window.open(blobUrl);
      if (!win) {
        throw new Error('Failed to open print window. Please check your popup blocker settings.');
      }
    } catch (error) {
      console.error('Error printing receipt:', error);
      setError(error instanceof Error ? error.message : 'Failed to print receipt. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewWithSelectedFeeTypes = async () => {
    setLoading(true);
    setError(null);

    try {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }

      const pdfData = await fetchPdfData('none', selectedFeeTypes);
      const { blobUrl } = await generateReceiptGroupPdf(pdfData);
      setPdfUrl(blobUrl);
      setPreviewOpen(true);
    } catch (error) {
      console.error('Error generating preview:', error);
      setError(error instanceof Error ? error.message : 'Failed to generate preview. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadWithSelectedFeeTypes = async () => {
    setLoading(true);
    setError(null);

    try {
      const pdfData = await fetchPdfData('none', selectedFeeTypes);
      const { blob } = await generateReceiptGroupPdf(pdfData);
      const filename = `ReceiptGroup-${receiptGroupRefNumber}-selected-fees.pdf`;
      saveAs(blob, filename);
    } catch (error) {
      console.error('Error downloading receipt:', error);
      setError(error instanceof Error ? error.message : 'Failed to download receipt. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!receiptGroupRefNumber) {
    return null;
  }

  return (
    <>
      {/* Print Options Dialog */}
      <Dialog open={open && !previewOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            bgcolor: 'success.main',
            color: 'white',
            fontWeight: 'bold',
            py: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PrintIcon />
            Receipt Print Options
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="bold">
              Payment successful! Receipt created: {receiptGroupRefNumber}
            </Typography>
          </Alert>

          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Choose how you want to view or save your receipt:
          </Typography>

          <Grid container spacing={2}>
            {/* Preview Options */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ mb: 1 }}>
                <VisibilityIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                Preview Options
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper
                sx={{
                  p: 2,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  '&:hover': {
                    bgcolor: loading ? 'inherit' : 'action.hover',
                    boxShadow: loading ? 1 : 3
                  },
                  transition: 'all 0.2s',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
                onClick={loading ? undefined : handlePreviewWithInstallments}
              >
                <Stack spacing={1} alignItems="center">
                  <VisibilityIcon color="primary" sx={{ fontSize: 40 }} />
                  <Typography variant="body2" fontWeight="bold" textAlign="center">
                    Preview with Installments
                  </Typography>
                  <Typography variant="caption" color="text.secondary" textAlign="center">
                    View complete receipt with all details
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper
                sx={{
                  p: 2,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  '&:hover': {
                    bgcolor: loading ? 'inherit' : 'action.hover',
                    boxShadow: loading ? 1 : 3
                  },
                  transition: 'all 0.2s',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
                onClick={loading ? undefined : handlePreviewWithoutInstallments}
              >
                <Stack spacing={1} alignItems="center">
                  <ReceiptIcon color="primary" sx={{ fontSize: 40 }} />
                  <Typography variant="body2" fontWeight="bold" textAlign="center">
                    Preview without Installments
                  </Typography>
                  <Typography variant="caption" color="text.secondary" textAlign="center">
                    View receipt summary only
                  </Typography>
                </Stack>
              </Paper>
            </Grid>

            {/* Print Options */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ mb: 1, mt: 1 }}>
                <PrintIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                Print Options
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper
                sx={{
                  p: 2,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  '&:hover': {
                    bgcolor: loading ? 'inherit' : 'action.hover',
                    boxShadow: loading ? 1 : 3
                  },
                  transition: 'all 0.2s',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
                onClick={loading ? undefined : handlePrintWithInstallments}
              >
                <Stack spacing={1} alignItems="center">
                  <PrintIcon color="info" sx={{ fontSize: 40 }} />
                  <Typography variant="body2" fontWeight="bold" textAlign="center">
                    Print with Installments
                  </Typography>
                  <Typography variant="caption" color="text.secondary" textAlign="center">
                    Print complete receipt details
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper
                sx={{
                  p: 2,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  '&:hover': {
                    bgcolor: loading ? 'inherit' : 'action.hover',
                    boxShadow: loading ? 1 : 3
                  },
                  transition: 'all 0.2s',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
                onClick={loading ? undefined : handlePrintWithoutInstallments}
              >
                <Stack spacing={1} alignItems="center">
                  <PrintIcon color="info" sx={{ fontSize: 40 }} />
                  <Typography variant="body2" fontWeight="bold" textAlign="center">
                    Print without Installments
                  </Typography>
                  <Typography variant="caption" color="text.secondary" textAlign="center">
                    Print receipt summary only
                  </Typography>
                </Stack>
              </Paper>
            </Grid>

            {/* Download Options */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ mb: 1, mt: 1 }}>
                <DownloadIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                Download Options
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper
                sx={{
                  p: 2,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  '&:hover': {
                    bgcolor: loading ? 'inherit' : 'action.hover',
                    boxShadow: loading ? 1 : 3
                  },
                  transition: 'all 0.2s',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
                onClick={loading ? undefined : handleDownloadWithInstallments}
              >
                <Stack spacing={1} alignItems="center">
                  <DownloadIcon color="success" sx={{ fontSize: 40 }} />
                  <Typography variant="body2" fontWeight="bold" textAlign="center">
                    Download with Installments
                  </Typography>
                  <Typography variant="caption" color="text.secondary" textAlign="center">
                    Save PDF with full details
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper
                sx={{
                  p: 2,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  '&:hover': {
                    bgcolor: loading ? 'inherit' : 'action.hover',
                    boxShadow: loading ? 1 : 3
                  },
                  transition: 'all 0.2s',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
                onClick={loading ? undefined : handleDownloadWithoutInstallments}
              >
                <Stack spacing={1} alignItems="center">
                  <DownloadIcon color="success" sx={{ fontSize: 40 }} />
                  <Typography variant="body2" fontWeight="bold" textAlign="center">
                    Download without Installments
                  </Typography>
                  <Typography variant="caption" color="text.secondary" textAlign="center">
                    Save PDF with receipts only
                  </Typography>
                </Stack>
              </Paper>
            </Grid>

            {/* Fee Type Selection Options */}
            {availableFeeTypes.length > 1 && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="secondary" fontWeight="bold" sx={{ mb: 1, mt: 1 }}>
                    <FilterListIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    Select Specific Fee Types (Without Installments)
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper
                    sx={{
                      p: 2,
                      cursor: loading ? 'not-allowed' : 'pointer',
                      '&:hover': {
                        bgcolor: loading ? 'inherit' : 'action.hover',
                        boxShadow: loading ? 1 : 3
                      },
                      transition: 'all 0.2s',
                      border: '1px solid',
                      borderColor: 'secondary.main'
                    }}
                    onClick={loading ? undefined : () => handleOpenFeeTypeSelector('preview')}
                  >
                    <Stack spacing={1} alignItems="center">
                      <VisibilityIcon color="secondary" sx={{ fontSize: 36 }} />
                      <Typography variant="body2" fontWeight="bold" textAlign="center">
                        Preview Selected Fees
                      </Typography>
                      <Typography variant="caption" color="text.secondary" textAlign="center">
                        Choose fee types to preview
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper
                    sx={{
                      p: 2,
                      cursor: loading ? 'not-allowed' : 'pointer',
                      '&:hover': {
                        bgcolor: loading ? 'inherit' : 'action.hover',
                        boxShadow: loading ? 1 : 3
                      },
                      transition: 'all 0.2s',
                      border: '1px solid',
                      borderColor: 'secondary.main'
                    }}
                    onClick={loading ? undefined : () => handleOpenFeeTypeSelector('print')}
                  >
                    <Stack spacing={1} alignItems="center">
                      <PrintIcon color="secondary" sx={{ fontSize: 36 }} />
                      <Typography variant="body2" fontWeight="bold" textAlign="center">
                        Print Selected Fees
                      </Typography>
                      <Typography variant="caption" color="text.secondary" textAlign="center">
                        Choose fee types to print
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper
                    sx={{
                      p: 2,
                      cursor: loading ? 'not-allowed' : 'pointer',
                      '&:hover': {
                        bgcolor: loading ? 'inherit' : 'action.hover',
                        boxShadow: loading ? 1 : 3
                      },
                      transition: 'all 0.2s',
                      border: '1px solid',
                      borderColor: 'secondary.main'
                    }}
                    onClick={loading ? undefined : () => handleOpenFeeTypeSelector('download')}
                  >
                    <Stack spacing={1} alignItems="center">
                      <DownloadIcon color="secondary" sx={{ fontSize: 36 }} />
                      <Typography variant="body2" fontWeight="bold" textAlign="center">
                        Download Selected Fees
                      </Typography>
                      <Typography variant="caption" color="text.secondary" textAlign="center">
                        Choose fee types to download
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
              </>
            )}
          </Grid>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3 }}>
              <CircularProgress size={30} />
              <Typography variant="body2" sx={{ ml: 2 }}>
                Processing...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            variant="outlined"
            startIcon={<CloseIcon />}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* PDF Preview Dialog */}
      <Dialog open={previewOpen} onClose={handleClosePreview} maxWidth="lg" fullWidth>
        <DialogTitle>
          Receipt Preview - {receiptGroupRefNumber}
        </DialogTitle>
        <DialogContent dividers sx={{ height: '80vh', p: 0 }}>
          {pdfUrl ? (
            <iframe
              title="Receipt PDF Preview"
              src={pdfUrl}
              style={{ border: 'none', width: '100%', height: '100%' }}
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button onClick={handleClosePreview} variant="outlined">
            Back to Options
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={handleOpenInNewTab}
              variant="outlined"
              startIcon={<VisibilityIcon />}
            >
              Open in New Tab
            </Button>
            <Button
              onClick={handlePrintWithInstallments}
              variant="contained"
              startIcon={<PrintIcon />}
            >
              Print
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Fee Type Selector Dialog */}
      <Dialog open={feeTypeSelectorOpen} onClose={handleCloseFeeTypeSelector} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            bgcolor: 'secondary.main',
            color: 'white',
            fontWeight: 'bold',
            py: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon />
            Select Fee Types
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Select the fee types you want to include in your receipt. Only receipts without installments will be filtered.
            </Typography>
          </Alert>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Button
              size="small"
              onClick={handleSelectAllFeeTypes}
              variant="outlined"
              color="secondary"
            >
              Select All
            </Button>
            <Button
              size="small"
              onClick={handleDeselectAllFeeTypes}
              variant="outlined"
              color="secondary"
            >
              Deselect All
            </Button>
          </Box>

          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
            <FormGroup>
              {availableFeeTypes.map((feeType) => (
                <FormControlLabel
                  key={feeType}
                  control={
                    <Checkbox
                      checked={selectedFeeTypes.includes(feeType)}
                      onChange={() => handleToggleFeeType(feeType)}
                      color="secondary"
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight="500">
                      {feeType}
                    </Typography>
                  }
                />
              ))}
            </FormGroup>
          </Paper>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            {selectedFeeTypes.length} of {availableFeeTypes.length} fee type(s) selected
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleCloseFeeTypeSelector}
            variant="outlined"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmFeeTypeSelection}
            variant="contained"
            color="secondary"
            disabled={loading || selectedFeeTypes.length === 0}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Processing...' : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PrintOptionsDialog;
